import { useEffect, useMemo, useRef } from 'react'
import { IDataPoint, TLastValuesDataRef } from '../data.types.ts'
import dataSubscriptionManager from '../data.subscriptionManager.ts'
import eventBus from '../../eventBus'
import dataApi, { TLastValuesResponse } from '../data.api.ts'
import { IParameter } from '@/pages/Parameters/parameters.types.ts'
import { IEquipment } from '@/pages/Equipment/equipment.types.ts'
import useHttpLoader from '../../../shared/hooks/useHttpLoader.ts'
import { useRecoilValue } from 'recoil'
import parametersSelectors from '../../../pages/Parameters/parameters.selectors.ts'
import { HTTPResponse } from '@/services/http/http.types.ts'
import equipmentSelectors from '@/pages/Equipment/equipment.selectors.ts'
import { TDeviceProtocol } from '@/services/ws/ws.types.ts'

export interface IUseLastValuesProps {
  parameters: Array<IParameter['id']>
  devices: Array<IEquipment['id']>
  id: string
  tsFrom: number
  live: boolean

  onNewValueReceived?: () => void
}

const useLastValuesRef = (props: IUseLastValuesProps) => {
  const { wait, loading } = useHttpLoader()
  const ref = useRef<TLastValuesDataRef>(generateEmptyValuesRef(props))
  const parametersMap = useRecoilValue(parametersSelectors.getParametersMap)
  const equipmentMap = useRecoilValue(equipmentSelectors.getEquipmentMap)

  const devicesSet = useMemo(() => {
    return new Set(props.devices)
  }, [props.devices])

  const parametersSet = useMemo(() => {
    return new Set(props.parameters)
  }, [props.parameters])

  const sourceIdToIdMap = useRecoilValue(parametersSelectors.getSourceIdToIdMap)

  useEffect(() => {
    if (!props.live) return

    dataSubscriptionManager.addSubscription({
      parameters: props.parameters,
      devices: props.devices,
      deviceProtocols: props.devices.map((deviceId) => equipmentMap[deviceId]?.protocolId as TDeviceProtocol),
      id: props.id,
      parametersSourceIds: props.parameters.map((p) => parametersMap[p]?.sourceId).filter(Boolean),
    })

    const dataUnsub = eventBus.sub('data.point', (point: IDataPoint) => {
      const { equipmentId, values } = point
      if (!devicesSet.has(equipmentId)) return

      let hasUpdatedValue = false
      for (const p of values) {
        if (!parametersSet.has(p.parameterId)) continue
        if (!ref.current[equipmentId]) ref.current[equipmentId] = {}

        hasUpdatedValue = true
        ref.current[equipmentId][p.parameterId] = p.value
      }

      if (props.onNewValueReceived && hasUpdatedValue) {
        props.onNewValueReceived()
      }
    })

    return () => {
      dataSubscriptionManager.removeSubscription(props.id)
      dataUnsub()
    }
  }, [devicesSet, parametersSet, props.onNewValueReceived, props.live])

  useEffect(() => {
    const job = async (): Promise<Array<HTTPResponse<TLastValuesResponse>>> => {
      return Promise.all(
        props.devices.map((deviceId) =>
          props.live
            ? dataApi.getLastValues({
                protocolId: equipmentMap[deviceId]?.protocolId,
                deviceId,
                parameters: props.parameters.map((p) => parametersMap[p]?.sourceId),
                sourceIdToIdMap: sourceIdToIdMap,
              })
            : dataApi.getLastValuesInPast({
                protocolId: equipmentMap[deviceId]?.protocolId,
                deviceId,
                parameters: props.parameters.map((p) => parametersMap[p]?.sourceId),
                sourceIdToIdMap: sourceIdToIdMap,
                ts: props.tsFrom,
              })
        )
      )
    }

    wait(job(), (responses) => {
      ref.current = generateEmptyValuesRef(props)

      for (let i = 0; i < responses.length; ++i) {
        if (responses[i].status === 'error') continue

        // Часть параметров могут быть получены по вебсокетам, пока шел запрос на получение последних данных.
        // Нужно выделить те данные, которые еще не были получены иным способом
        const deviceId = props.devices[i]
        for (const p of props.parameters) {
          if (ref.current[deviceId][p] === null) {
            ref.current[deviceId][p] = responses[i].body[deviceId][p]
          }
        }
      }

      if (props.onNewValueReceived) {
        props.onNewValueReceived()
      }
    })
  }, [devicesSet, props.live, props.tsFrom])

  return { ref, loading }
}

const generateEmptyValuesRef = (props: IUseLastValuesProps) => {
  const data: TLastValuesDataRef = {}

  for (const e of props.devices) {
    data[e] = {}
    for (const p of props.parameters) {
      data[e][p] = null
    }
  }

  return data
}

export default useLastValuesRef
